import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { Loader, Alert, Modal } from '@grandvisionhq/common';
import { useDispatch, useSelector, useFeatureToggles, useIntl } from '@grandvisionhq/state';
import { useDynamicModule } from '@grandvisionhq/utils';
import { useSetStore } from '../../../hooks/operations/use-set-store.js';
import { resetStore } from '../../../StepShipping/actions.js';
import { getSelectedShippingMethod, getServiceStore, shouldValidateServiceStore, getBillingAddress, getShippingDetails } from '../../../StepShipping/selectors.js';
import { useNearestStore } from '../hooks/use-nearest-store.js';
import { ActionButtonContainer } from './ActionButtonContainer.js';
import { StoreFinderItemContainer } from './StoreFinderItemContainer.js';
import { StoreSummaryContainer } from './StoreSummaryContainer.js';
import { isPickupStore } from '../../../utils/shipping-method.js';
import { useCheckoutSettings } from '../../../context/CheckoutSettings/context.js';

const loadModule = function() {
    var _ref = swcHelpers.asyncToGenerator(function*() {
        const storesModule = yield import(/* webpackChunkName: 'grandvisionhq-stores-v2' */ '@grandvisionhq/stores-v2');
        return storesModule;
    });
    return function loadModule() {
        return _ref.apply(this, arguments);
    };
}();
const usePickupStoresModule = ()=>useDynamicModule(loadModule, '@grandvisionhq/stores-v2', [])
;
const ServiceStoreContainer = ()=>{
    const [showModal, setShowModal] = React.useState(false);
    const [serviceStoreCode, setServiceStoreCode] = React.useState();
    const dispatch = useDispatch();
    const { setStore  } = useSetStore({
        onError: ()=>{
            setServiceStoreCode(null);
            dispatch(resetStore());
        }
    });
    const { serviceStore: serviceStoreConfig = {}  } = useCheckoutSettings();
    const selectedShippingMethod = useSelector(getSelectedShippingMethod);
    const serviceStore = useSelector(getServiceStore);
    const validateServiceStore = useSelector(shouldValidateServiceStore);
    const address = useSelector(getBillingAddress);
    const details = useSelector(getShippingDetails);
    const isPickupStoreSelected = isPickupStore(selectedShippingMethod === null || selectedShippingMethod === void 0 ? void 0 : selectedShippingMethod.method);
    const serviceStoreIsInValid = validateServiceStore && !serviceStore;
    const nearestStoreQuery = React.useMemo(()=>{
        if (details === null || details === void 0 ? void 0 : details.billingAddress.isValid) {
            const postalCode = (address === null || address === void 0 ? void 0 : address.postalCode) || '';
            const streetName = (address === null || address === void 0 ? void 0 : address.streetName) || '';
            const streetNumber = (address === null || address === void 0 ? void 0 : address.streetNumber) || '';
            const city = (address === null || address === void 0 ? void 0 : address.city) || '';
            return `${postalCode} ${streetName} ${streetNumber} ${city}`.replace(/\s{2,}/g, ' ').trim();
        }
        return '';
    }, [
        address,
        details
    ]);
    // Only attempt a service store preselect when the feature is enabled and there isn't already code selected.
    const [code, isLoading] = useNearestStore(nearestStoreQuery, !!serviceStoreConfig.preselectFromShippingData && !serviceStore);
    React.useEffect(()=>{
        if (code && !serviceStoreCode) {
            setServiceStoreCode(code);
        }
    }, [
        serviceStoreCode,
        code,
        isPickupStoreSelected
    ]);
    const [module, loading, error] = usePickupStoresModule();
    const { features  } = useCheckoutSettings();
    const featuresToggles = useFeatureToggles();
    const { getLabel  } = useIntl();
    const labels = React.useMemo(()=>({
            title: getLabel('checkout.serviceStore.title'),
            subtitle: getLabel('checkout.serviceStore.subtitle'),
            errorMessage: getLabel('checkout.serviceStore.error')
        })
    , [
        getLabel
    ]);
    const onStoreSelect = React.useCallback((store)=>{
        setShowModal(false);
        setServiceStoreCode(store === null || store === void 0 ? void 0 : store.code);
    }, []);
    const onEdit = React.useCallback(()=>{
        setShowModal(true);
    }, []);
    const onCloseModal = React.useCallback(()=>{
        setShowModal(false);
    }, []);
    const resetServiceStore = React.useCallback(()=>{
        setServiceStoreCode(null);
    }, []);
    React.useEffect(()=>{
        if (serviceStore) {
            setServiceStoreCode(serviceStore.code);
            setStore(serviceStore.code);
            return;
        }
    }, [
        serviceStore,
        setStore
    ]);
    if (!(features === null || features === void 0 ? void 0 : features.isServiceStoreEnabled) || isPickupStoreSelected) {
        return null;
    }
    if (loading) {
        return /*#__PURE__*/ React.createElement(Loader, null);
    }
    if (error || !module) {
        return /*#__PURE__*/ React.createElement(Alert, {
            type: "error",
            notifyScreenReader: false,
            message: labels.errorMessage
        });
    }
    const { Components , Containers , StoreFinderProvider  } = module;
    const { StoreFinder , PickUpButtons , withPickUpButtonsConfig  } = Containers;
    const { StoreListTopContent  } = Components;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, serviceStoreIsInValid && /*#__PURE__*/ React.createElement(Alert, {
        icon: "info",
        type: "error",
        notifyScreenReader: false,
        message: labels.errorMessage
    }), serviceStoreCode ? /*#__PURE__*/ React.createElement(StoreSummaryContainer, {
        storeCode: serviceStoreCode,
        actions: {
            onEdit
        },
        resetServiceStore: resetServiceStore
    }) : /*#__PURE__*/ React.createElement(ActionButtonContainer, {
        onClick: onEdit,
        isLoading: isLoading,
        isValid: !serviceStoreIsInValid
    }), /*#__PURE__*/ React.createElement(Modal, {
        className: "service-store__modal",
        showIcon: true,
        iconClassName: "service-store__modal__close",
        isOpen: showModal,
        onModalClose: onCloseModal
    }, /*#__PURE__*/ React.createElement(StoreFinderProvider, {
        googleMapsId: "google-maps-service-store",
        hidePhone: !featuresToggles.isStorePickupPhoneEnabled,
        hideEmail: !featuresToggles.isStorePickupEmailEnabled
    }, /*#__PURE__*/ React.createElement(StoreFinder, {
        isInsideModal: true,
        listTopContent: /*#__PURE__*/ React.createElement(StoreListTopContent, {
            title: labels.title,
            description: labels.subtitle
        }),
        listItemComponent: (props)=>/*#__PURE__*/ React.createElement(StoreFinderItemContainer, swcHelpers.extends({}, props, {
                variant: 'list-item',
                onClick: onStoreSelect
            }))
        ,
        infoWindowContentComponent: (props)=>/*#__PURE__*/ React.createElement(StoreFinderItemContainer, swcHelpers.extends({}, props, {
                variant: 'map-item',
                onClick: onStoreSelect
            }))
        ,
        buttonsComponent: withPickUpButtonsConfig(PickUpButtons, {
            onStoreSelect
        })
    }))));
};

export { ServiceStoreContainer };
//# sourceMappingURL=ServiceStoreContainer.js.map
